import classNames from 'classnames';
import { PaginationProps } from './Pagination.interface';
import { ReactNode, useEffect, useState, KeyboardEvent, useMemo } from 'react';
import './Pagination.sass';
import { IcAngleRight16 } from '$assets';
import { Input } from '$uikit';
import Select from 'react-select';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 30, 50, 100];

export const Pagination = ({
  currentPage,
  totalCount,
  onChangePage,
  pageSizeConfig,
}: PaginationProps) => {
  const [pageInputValue, setPageInputValue] = useState(currentPage);
  const selectPageSizeOptions = (
    pageSizeConfig?.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS
  )
    .filter((size, index, sizes) => {
      const elementCount = pageSizeConfig?.totalItemsCount;

      if (!elementCount) {
        return true;
      }

      if (index === 0) {
        return elementCount >= size;
      }

      return elementCount >= sizes[index - 1];
    })
    .map((item) => ({
      value: item,
      label: item,
    }));
  const isPageSizeControlEnabled =
    !pageSizeConfig?.totalItemsCount || pageSizeConfig?.totalItemsCount >= 10;

  useEffect(() => {
    setPageInputValue(currentPage);
  }, [currentPage]);

  const generatePageButton = (pageNumber: number) => {
    return (
      <div
        key={pageNumber}
        className={classNames('Pagination__page__button', {
          _active: pageNumber === currentPage,
        })}
        onClick={() => {
          onChangePage(pageNumber);
        }}
      >
        <span>{pageNumber}</span>
      </div>
    );
  };

  const handleInputKeyDown = ({ key }: KeyboardEvent) => {
    if (key === 'Enter') {
      if (+pageInputValue > totalCount) {
        onChangePage(totalCount);
        setPageInputValue(totalCount);
        return;
      }

      if (+pageInputValue < 1) {
        onChangePage(1);
        setPageInputValue(1);
        return;
      }

      onChangePage(+pageInputValue);
    }
  };

  function renderElementsCountLabel() {
    if (!pageSizeConfig?.totalItemsCount) return null;

    const { pageSize, totalItemsCount } = pageSizeConfig;

    const firstPageElement = pageSize * (currentPage - 1);
    return (
      <span className="Pagination__pageSize__elementsCount">
        Showing {firstPageElement + 1}-
        {currentPage === totalCount ? totalItemsCount : firstPageElement + pageSize} of{' '}
        {totalItemsCount}
      </span>
    );
  }

  function renderPages() {
    const pages: ReactNode[] = [];

    if (totalCount <= 6) {
      for (let i = 1; i <= totalCount; i++) {
        pages.push(generatePageButton(i));
      }
    } else {
      pages.push(generatePageButton(1));

      if (currentPage > 3) {
        pages.push(
          <div key={-1} className="Pagination__dots">
            ...
          </div>,
        );
      }

      if (currentPage == totalCount) {
        pages.push(generatePageButton(currentPage - 2));
      }

      if (currentPage > 2) {
        pages.push(generatePageButton(currentPage - 1));
      }

      if (currentPage != 1 && currentPage != totalCount) {
        pages.push(generatePageButton(currentPage));
      }

      if (currentPage < totalCount - 1) {
        pages.push(generatePageButton(currentPage + 1));
      }

      if (currentPage === 1) {
        pages.push(generatePageButton(currentPage + 2));
      }

      if (currentPage < totalCount - 2) {
        pages.push(
          <div key={-2} className="Pagination__dots">
            ...
          </div>,
        );
      }

      if (totalCount > 1) {
        pages.push(generatePageButton(totalCount));
      }
    }

    return pages;
  }

  return (
    <div className="Pagination">
      {!!pageSizeConfig && (
        <div className="Pagination__pageSize">
          {renderElementsCountLabel()}
          {isPageSizeControlEnabled && (
            <div className="Pagination__pageSizeControl">
              <span className="Pagination__Select__label">Results per page</span>
              <div className="Pagination__Select__container">
                <Select
                  menuPlacement="top"
                  classNamePrefix="Select"
                  options={selectPageSizeOptions}
                  onChange={(action) => {
                    action && pageSizeConfig.onChangePageSize(action.value);
                  }}
                  value={selectPageSizeOptions.find(
                    (option) => pageSizeConfig.pageSize === option.value,
                  )}
                />
              </div>
            </div>
          )}
        </div>
      )}
      {totalCount > 1 && (
        <div className="Pagination__pageCount">
          <Input
            padding={4}
            minHeight={32}
            type="number"
            value={pageInputValue.toString()}
            onChange={({ target }) => {
              setPageInputValue(+(target as HTMLInputElement).value);
            }}
            onBlur={() => setPageInputValue(currentPage)}
            onKeyDown={handleInputKeyDown}
          />
          <div className="Pagination__pageCountControl">
            <div
              className={classNames('Pagination__arrow _left', {
                _disabled: currentPage === 1,
              })}
              onClick={() => onChangePage(currentPage - 1)}
            >
              <IcAngleRight16 />
            </div>{' '}
            {renderPages()}
            <div
              className={classNames('Pagination__arrow', {
                _disabled: currentPage === totalCount,
              })}
              onClick={() => onChangePage(currentPage + 1)}
            >
              <IcAngleRight16 />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
